/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
'use client';

import { Popover, Switch, Transition } from '@headlessui/react';
import {
    mdiClose,
    mdiDelete,
    mdiHumanGreeting,
    mdiHumanMaleBoard,
    mdiTuneVariant,
    mdiCheckBold,
    mdiCloseThick,
} from '@mdi/js';
import Icon from '@mdi/react';
import { useEffect } from 'react';
import useLocalStorage from '@/hooks/useLocalStorage';
import localFont from 'next/font/local';
import { Subject } from '@/hooks/useSubject';
import IconButton from '@/components/Button/IconButton';
import { slugify } from '@/utils/strings';
import { useSubjectProgress } from '@/hooks/useSubjectProgress';
import { getTagValueByKey } from '@/utils/tags';

const openDyslexic = localFont({
    src: [
        {
            path: './fonts/OpenDyslexic-Regular.woff2',
            weight: '400',
            style: 'normal',
        },
        {
            path: './fonts/OpenDyslexic-Bold.woff2',
            weight: '700',
            style: 'normal',
        },
        {
            path: './fonts/OpenDyslexic-Italic.woff2',
            weight: '400',
            style: 'italic',
        },
    ],
});

const enableDyslexiaFont = (enabled: boolean) => {
    if (enabled) {
        document.body.classList.add(openDyslexic.className);
    } else {
        document.body.classList.remove(openDyslexic.className);
    }
};

const AccessibilityMenu = ({
    subject,
    panelClassName = '',
    buttonClassName = '',
    buttonIcon = mdiTuneVariant,
    buttonIsExtraContrast = false,
}: {
    subject: Subject;
    panelClassName?: string;
    buttonClassName?: string;
    buttonIcon?: string;
    buttonIsExtraContrast?: boolean;
}) => {
    const { resetSubjectProgress, setHasSeenIntro } = useSubjectProgress(
        subject.id,
    );

    const [isStaticChatTimelineEnabled, setIsStaticChatTimelineEnabled] =
        useLocalStorage('isStaticChatTimelineEnabled', false);

    const [isFocusModeEnabled, setIsFocusModeEnabled] = useLocalStorage(
        'isFocusModeEnabled',
        false,
    );

    const [
        isScreenReaderAnnouncementsEnabled,
        setIsScreenReaderAnnouncementsEnabled,
    ] = useLocalStorage('isScreenReaderAnnouncementsEnabled', true);

    return (
        <>
            <Popover>
                <IconButton
                    isPopoverButton
                    className={buttonClassName}
                    title="Opties en toegankelijkheid"
                    icon={buttonIcon}
                    isExtraContrast={buttonIsExtraContrast}
                />

                <Transition
                    as="div"
                    enter="transition duration-300 ease-out"
                    enterFrom="motion-safe:translate-x-full opacity-0"
                    enterTo="opacity-100"
                    leave="transition duration-300 ease-out"
                    leaveFrom="opacity-100"
                    leaveTo="motion-safe:translate-x-full opacity-0"
                    className={`absolute inset-0 z-10 h-full shadow-2xl ${panelClassName}`}
                >
                    <Popover.Panel
                        className={`absolute inset-0 z-10 h-full bg-primary-600 text-white md:rounded-tl-lg overflow-y-auto overflow-x-hidden ${panelClassName}`}
                    >
                        {({ close }) => (
                            <>
                                <SettingsSection
                                    title="Algemeen"
                                    hasCloseButton
                                    close={close}
                                >
                                    <EmbeddableTextAccessibilitySettings />
                                    <SettingSwitch
                                        checked={isFocusModeEnabled}
                                        onChange={(checked) => {
                                            setIsFocusModeEnabled(checked);
                                        }}
                                        label="Meer ruimte"
                                    />
                                    <SettingSwitch
                                        checked={isStaticChatTimelineEnabled}
                                        onChange={(checked) => {
                                            setIsStaticChatTimelineEnabled(
                                                checked,
                                            );
                                        }}
                                        label="Chat uitvouwen"
                                    />
                                </SettingsSection>

                                <SettingsSection title="Schermlezers">
                                    <SettingSwitch
                                        checked={
                                            isScreenReaderAnnouncementsEnabled
                                        }
                                        onChange={(checked) => {
                                            setIsScreenReaderAnnouncementsEnabled(
                                                checked,
                                            );
                                        }}
                                        label={
                                            <>
                                                Chat aankondigingen
                                                <span className="sr-only">
                                                    (pagina moet hiervoor
                                                    misschien herladen worden)
                                                </span>
                                            </>
                                        }
                                    />
                                </SettingsSection>

                                <SettingsSection title={subject.title}>
                                    <SettingsButton
                                        label="Intro openen"
                                        icon={mdiHumanGreeting}
                                        onClick={() => {
                                            setHasSeenIntro(false);
                                        }}
                                    />
                                    {!!getTagValueByKey(
                                        subject,
                                        'lesbrief_link',
                                    ) && (
                                        <SettingsButton
                                            label="Naar de lesbrief"
                                            icon={mdiHumanMaleBoard}
                                            onClick={() => {
                                                window.open(
                                                    getTagValueByKey(
                                                        subject,
                                                        'lesbrief_link',
                                                    ),
                                                    '_blank',
                                                );
                                            }}
                                        />
                                    )}
                                    <SettingsButton
                                        label="Voortgang wissen"
                                        icon={mdiDelete}
                                        onClick={() => {
                                            if (
                                                confirm(
                                                    `Weet je het zeker dat je alle voortgang van alle pagina's van ${subject.title}?`,
                                                )
                                            ) {
                                                resetSubjectProgress();
                                                window.location.href = `/${
                                                    subject.id
                                                }/${slugify(subject.title)}`;
                                            }
                                        }}
                                    />
                                </SettingsSection>
                            </>
                        )}
                    </Popover.Panel>
                </Transition>
            </Popover>
            <button
                className="sr-only left-4 border-4 border-primary-50 bg-primary-700 text-white focus:px-4 focus:not-sr-only focus:fixed focus:top-4 focus:z-200"
                onClick={() => {
                    // Move keyboard focus to the chat
                    const scroller = document.querySelector(
                        '#accessibletimelinescroller',
                    );
                    if (scroller) {
                        scroller.setAttribute('tabindex', '0');
                        (scroller as HTMLElement).focus();
                        (scroller as HTMLElement).onblur = () => {
                            scroller.removeAttribute('tabindex');
                        };
                    }
                }}
            >
                Direct naar chat gaan
            </button>
        </>
    );
};

const SettingsSection = ({
    title,
    children,
    hasCloseButton = false,
    close,
}: {
    title: string;
    children: React.ReactNode;
    hasCloseButton?: boolean;
    close?: () => void;
}) => {
    return (
        <figure>
            <figcaption>
                <h2
                    className={`flex items-center justify-between bg-primary-700 pl-4 p-2 text-xl text-white shadow-md ${
                        hasCloseButton ? 'md:rounded-tl-lg' : ''
                    }`}
                >
                    <span className="text-lg py-2">{title}</span>
                    {hasCloseButton && (
                        <IconButton
                            icon={mdiClose}
                            title="Opties sluiten"
                            className="-m-2"
                            onClick={() => close?.()}
                        />
                    )}
                </h2>
            </figcaption>
            <ul>{children}</ul>
        </figure>
    );
};

type SettingsSwitchVariant = 'dark' | 'light';

const SettingSwitch = ({
    label,
    checked,
    variant = 'dark',
    onChange,
}: {
    label: string | React.ReactNode;
    checked: boolean;
    variant?: SettingsSwitchVariant;
    onChange: (checked: boolean) => void;
}) => {
    const colors: Record<
        SettingsSwitchVariant,
        {
            background: string;
            circle: string;
            icon: string;
        }
    > = {
        light: {
            background: 'bg-primary-700',
            circle: 'bg-white',
            icon: 'text-primary-text-base-aa',
        },
        dark: {
            background: checked ? 'bg-white shadow' : 'bg-primary-700',
            circle: checked ? 'bg-primary-text-base-aa' : 'bg-white',
            icon: checked ? 'text-white' : 'text-primary-text-base-aa',
        },
    };

    return (
        <Switch.Group>
            <div
                className="group flex cursor-pointer items-center border-b-2 border-white/25 p-4 transition-all last:border-b-0 hover:bg-white/10"
                onClick={() => onChange(!checked)}
            >
                <Switch
                    checked={checked}
                    onChange={onChange}
                    onClick={(event) => {
                        // Block the click event from propagating to the parent
                        event.stopPropagation();
                    }}
                    className={`${colors[variant].background} shrink-0 inline-flex h-6 w-10 origin-right items-center rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-text-base-aa focus-visible:ring-offset-2 motion-safe:group-active:scale-90`}
                >
                    <span
                        className={`${colors[variant].circle} ${
                            checked
                                ? 'translate-x-[1.125rem]'
                                : 'translate-x-0.5'
                        } inline-flex items-center justify-center h-5 w-5 rounded-full transition-transform`}
                    >
                        <Icon
                            path={checked ? mdiCheckBold : mdiCloseThick}
                            size={0.7}
                            className={colors[variant].icon}
                        />
                    </span>
                </Switch>
                <Switch.Label className="ml-4 origin-left cursor-pointer text-base font-bold select-none transition-all motion-safe:group-active:scale-95">
                    {label}
                </Switch.Label>
            </div>
        </Switch.Group>
    );
};

export const SettingsButton = ({
    label,
    icon,
    onClick,
}: {
    label: string;
    icon: string;
    onClick: () => void;
}) => (
    <button
        className="group flex items-center justify-between w-full border-b-2 border-white/25 text-base transition-all hover:bg-white/10 focus-visible:inner-border-2 focus-visible:inner-border-white"
        onClick={onClick}
    >
        <span className="flex items-center gap-2 p-4 transition-all motion-safe:active:scale-95">
            <Icon path={icon} size={1} />
            {label}
        </span>
    </button>
);

export const EmbeddableTextAccessibilitySettings = ({
    variant = 'dark',
}: {
    variant?: SettingsSwitchVariant;
}) => {
    const [isDyslexiaFontEnabled, setIsDyslexiaFontEnabled] = useLocalStorage(
        'isDyslexiaFontEnabled',
        false,
    );

    useEffect(() => {
        enableDyslexiaFont(isDyslexiaFontEnabled);
    }, [isDyslexiaFontEnabled]);

    useEffect(() => {
        enableDyslexiaFont(isDyslexiaFontEnabled);
    }, []);

    const [isIncreasedTextSizeEnabled, setIsIncreasedTextSizeEnabled] =
        useLocalStorage('isIncreasedTextSizeEnabled', false);

    return (
        <>
            <SettingSwitch
                variant={variant}
                checked={isDyslexiaFontEnabled}
                onChange={(checked) => {
                    setIsDyslexiaFontEnabled(checked);
                }}
                label="Dyslexie-lettertype"
            />
            <SettingSwitch
                variant={variant}
                checked={isIncreasedTextSizeEnabled}
                onChange={(checked) => {
                    setIsIncreasedTextSizeEnabled(checked);
                }}
                label="Berichten vergroten"
            />
        </>
    );
};

export default AccessibilityMenu;
